import { defineModule } from '../../engine/module';
import type { Client } from '../../engine/api/client';
import type { IconName } from '../../engine/design/icons';
import type { SearchHit } from '../../engine/module';
import { HomeScreen } from './HomeScreen';
import { DashboardsScreen } from './DashboardsScreen';
import { homeTiles } from './tiles';

// The home module's UI (docs/integration.md): the root route, the ⌘K record search wired to the
// server-side aggregate (GET /v1/search — one round trip instead of one list call per resource),
// and the cross-module quick commands. Every module already contributes its own "New …"
// commands; Home adds the ones that span modules.

export interface SearchResponse {
  q: string;
  groups: { resource: string; label: string; icon: string | null; items: { id: string; label: string; ref: string | null; sub: string | null; route: string }[] }[];
  omitted: string[];
}

/** Map the aggregate search response onto palette hits (grouped order preserved). */
export function hitsOf(res: SearchResponse): SearchHit[] {
  return res.groups.flatMap((g) =>
    g.items.map((h) => ({
      id: `${g.resource}:${h.id}`,
      label: h.ref && h.ref !== h.label ? `${h.ref} · ${h.label}` : h.label,
      kind: g.label.toLowerCase().replace(/s$/, ''),
      icon: (g.icon as IconName | null) ?? 'file',
      keywords: h.sub ?? undefined,
      to: h.route,
    })),
  );
}

export async function searchRecords(client: Client, q: string): Promise<SearchHit[]> {
  const res = await client.request<SearchResponse>('/v1/search', { query: { q, limit: 5 } });
  return hitsOf(res);
}

export const homeModule = defineModule({
  name: 'home',
  // No nav entry: Home is reached by the brand logo (which links to "/") and the ⌘K "Home"
  // command below — so it doesn't spawn a one-item "Overview" area with an empty sidebar.
  // Dashboards sit beside Reports in the Insight section (which the shell merges across modules).
  nav: [{ label: 'Insight', order: 30, items: [{ label: 'Dashboards', to: '/dashboards', icon: 'trending-up', key: 'g', permission: 'reports:read' }] }],
  screens: [
    { path: '/', resource: '', kind: 'custom', title: 'Home', component: HomeScreen },
    { path: '/dashboards', resource: '', kind: 'custom', title: 'Dashboards', component: DashboardsScreen },
  ],
  commands: [
    { id: 'home', label: 'Home', kind: 'go to', icon: 'home', keywords: 'dashboard overview today', run: ({ navigate }) => navigate('/') },
    { id: 'dashboards', label: 'Dashboards', kind: 'go to', icon: 'trending-up', keywords: 'kpi mrr utilisation mttr fcr csat metrics', run: ({ navigate }) => navigate('/dashboards') },
    { id: 'home-my-work', label: 'My work today', kind: 'go to', icon: 'sun', keywords: 'due tickets tasks approvals', run: ({ navigate }) => navigate('/#today') },
  ],
  search: searchRecords,
  tiles: homeTiles,
});
